import { useEffect, useMemo, useState } from 'react';
import { api } from './api';

/**
 * THE BODY A ROSTER ENTRY SHOULD ACTUALLY WEAR.
 *
 * `roster.json` is the METADATA register — post, height, gait tuning, outfits,
 * all hand-tuned and all still canon. What it is NOT is the current model: the
 * bodies moved to the server (Khari, 2026-08-24) so the deploy could stop
 * shipping ~67MB of GLBs, and `public/models/avatars/` has been drifting behind
 * ever since. Measured 2026-08-31: **8 of 14 roster bodies have a newer build on
 * the server** (rebel, avva, cosmina, nyjah, spor1, junex, samzie, malaki).
 *
 * `NpcManager` had this right for the street and nowhere else, inline. Khari,
 * 2026-08-31: *"the placed residents in the rooms… are using the older 3D models
 * instead of the newer ones."* — because `Interior`, `InteriorResidents` and
 * `ListeningSeats` each fetch roster.json and mount its LOCAL paths. So the rule
 * lives here now and all four read it.
 */

const EMPTY = new Map();
let inflight = null;

/**
 * Every player/roster body the API knows about, by avatarId. The WHOLE row —
 * files, pose and gesture map — because they are not separable (see below).
 *
 * ⚠ ONE FETCH FOR THE WHOLE SESSION. Four surfaces mounting several bodies each
 * would otherwise re-request the list per body. A failure is NOT cached: the
 * promise is dropped so a later mount retries, because the alternative is one
 * bad moment at startup costing every body until reload.
 *
 * @returns {Promise<Map<number, object>>}
 */
export function loadBodyRigs() {
  if (!inflight) {
    inflight = api.avatarModels()
      .then((models) => new Map((models || [])
        .filter((m) => m.avatarId != null)
        .map((m) => [m.avatarId, m])))
      .catch((e) => {
        inflight = null;
        console.warn('[body] could not load server-side bodies:', e.message);
        return EMPTY;
      });
  }
  return inflight;
}

/**
 * Layer the server row onto a roster entry — **wholesale, or not at all.**
 *
 * ⚠ NEVER MIX FILE SETS. A clip animates translation and scale in the DONOR
 * body's proportions, so a remote mesh driven by a local walk from another
 * generation warps — that is how Cosmina got bent. A row missing its walk simply
 * stands. No row at all leaves the local files working exactly as before.
 *
 * ⚠ AND `pose`/`clips` TRAVEL WITH THE FILES, for the same reason: the face box
 * is in THAT mesh's skull heights and the rig corrections are for THAT bind, so
 * a body wearing one generation's mesh and another's tuning is wrong in a way
 * that looks like bad tuning rather than a mismatch. Two separate bugs on
 * 2026-08-31 came from dropping one of these in a hand-written merge — if you
 * add a field to the row, add it here.
 *
 * The roster keeps everything else: `post`, `rotationY`, `height`, gait, outfit.
 */
export function withRemoteBody(entry, row) {
  if (!entry || !row?.rigged) return entry;
  return {
    ...entry,
    rigged: row.rigged,
    walk: row.walk || null,
    idle: row.idle || null,
    pose: row.pose || null,
    clips: row.clips || null,
    // How the body presents, as its owner set it (API avatar-models `presents`).
    presents: row.presents ?? entry.presents ?? null,
    remoteModel: true,
  };
}

/** The row map as state, for a component that mounts bodies. */
export function useBodyRigs() {
  const [rigs, setRigs] = useState(EMPTY);
  useEffect(() => {
    let live = true;
    loadBodyRigs().then((m) => { if (live) setRigs(m); });
    return () => { live = false; };
  }, []);
  return rigs;
}

/**
 * A whole roster list with the server's bodies layered on, memoised so the
 * enriched entries keep a stable identity across renders.
 */
export function useRemoteBodies(roster) {
  const rigs = useBodyRigs();
  return useMemo(
    () => (roster || []).map((n) => withRemoteBody(n, rigs.get(n.avatarId))),
    [roster, rigs],
  );
}

/**
 * EVERY BODY THAT CAN STAND IN A ROOM (Khari, 2026-10-31: "when trying to set an
 * artist/resident into a room, what qualifies them? I have artists missing from
 * this list."). The rooms read roster.json alone — a hand-kept file, 14 bodies,
 * last touched 2026-10-23 — so an artist whose body was built since in the
 * artist editor (it exists on the server only) walked the street and could never
 * be placed indoors: Aryze, Alma Sazon, Abra Hamm on that day. Now: roster.json's
 * entries on their server bodies (as above) PLUS every rigged server body the
 * roster does not have, under the server's own slug (`avatar-<id>`), which is
 * what a placement saves as its `ref`. The roster wins a collision: a hand-tuned
 * entry stays canon.
 */
export function useRoomRoster(roster) {
  const rigs = useBodyRigs();
  return useMemo(() => {
    const list = (roster || []).map((n) => withRemoteBody(n, rigs.get(n.avatarId)));
    const have = new Set(list.map((n) => n.avatarId));
    for (const m of rigs.values()) {
      if (!m?.rigged || !m.slug || have.has(m.avatarId)) continue;
      list.push({
        slug: m.slug, avatarId: m.avatarId, name: m.name || m.slug, height: m.height || null,
        rigged: m.rigged, walk: m.walk || null, idle: m.idle || null, pose: m.pose || null, clips: m.clips || null,
        remoteModel: true, serverOnly: true,
      });
    }
    return list;
  }, [roster, rigs]);
}

export default useRemoteBodies;
